import { _breakP } from '../common/js/lib/utils.js';
import Swiper from 'swiper';
import { EffectFade, Autoplay, A11y } from 'swiper/modules';

export default class TopPage {
  constructor() {
    this.mainVisual();
    this.mainVisualSlider();
    this.mainVisualScrollProgress();
    this.commonSpaceSwitcher();
    this.planSectStacking();
    this.locationHorizontalScroll();
  }

  // location の横スクロール（GSAP 不使用）。
  // intro + pin をまとめた __sticky を、下端が画面下端に付く位置（top = 100vh - sticky高さ）
  // で sticky 固定（intro も一体で固定される）。.locationSect 自体を
  // 「sticky高さ + 横移動距離」の縦バッファにして、その区間で track を左へ流す。
  locationHorizontalScroll() {
    const section = document.querySelector('.js-locationScroll');
    if (!section) return;
    const pin = section.querySelector('.js-locationScroll-pin');
    const track = section.querySelector('.js-locationScroll-track');
    if (!pin || !track) return;
    const firstRow = track.querySelector('.locationSect__row');
    if (!firstRow) return;

    let distance = 0;
    let ticking = false;
    let lastX = null;

    // 完全には左へ流し切らず、終了位置を手前で止める。
    // visible の係数を小さく（マイナス）にするほど終了位置が左へ、大きくすると右へ。
    // SP は端の画像（※3）まで入るよう PC より大きく左へ流す。
    const measure = () => {
      const visible = (track.parentElement || pin).clientWidth;
      const coef = _breakP.SP ? -0.3 : 0.5;
      distance = Math.max(0, track.scrollWidth - visible * coef);
    };

    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      const rowRect = firstRow.getBoundingClientRect();
      const pinRect = pin.getBoundingClientRect();
      // 1個目の __row の上端が画面下端に来た時点（rowRect.top === vh）を 0、
      // __pin の下端が画面上端に来た時点（pinRect.bottom === 0）を 1 とする。
      const span = vh + pinRect.bottom - rowRect.top;
      const progress = span > 0
        ? Math.max(0, Math.min(1, (vh - rowRect.top) / span))
        : 0;
      const x = (-progress * distance).toFixed(1);
      if (x !== lastX) {
        track.style.transform = `translate3d(${x}px, 0, 0)`;
        lastX = x;
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const onResize = () => {
      measure();
      update();
    };

    measure();
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('lenis-scroll', onScroll);
    window.addEventListener('resize', onResize);

    // 画像ロードなどでトラック幅が変わったら距離を取り直す
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => onResize());
      ro.observe(track);
      ro.observe(pin);
    } else {
      track.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', onResize, { once: true });
      });
    }
  }

  mainVisual() {
    const mainVisual = document.getElementById('mainVisual');
    if (!mainVisual) return;

    mainVisual.classList.add('is-loaded');
  }

  mainVisualSlider() {
    const el = document.querySelector('.js-mainVisualSlider');
    if (!el) return;

    new Swiper(el, {
      modules: [EffectFade, Autoplay, A11y],
      effect: 'fade',
      fadeEffect: { crossFade: true },
      loop: true,
      speed: 1500,
      allowTouchMove: false,
      autoplay: {
        delay: 5000,
        disableOnInteraction: false
      },
      a11y: {
        enabled: true,
        prevSlideMessage: '前のスライドへ',
        nextSlideMessage: '次のスライドへ'
      }
    });
  }

  mainVisualScrollProgress() {
    // お知らせ以降が載る container がせり上がるのに合わせて MV をぼかす。
    const container = document.querySelector('.main > .container');
    if (!container) return;

    const mainVisual = document.getElementById('mainVisual');
    const root = document.documentElement;
    // info / movie の opacity が 0 になる progress（clamp(0, 1 - mv-progress*3, 1) より 1/3）
    const fadeOutAt = 1 / 3;
    let ticking = false;
    let lastProgress = -1;

    const update = () => {
      ticking = false;
      const vh = window.innerHeight;
      if (vh <= 0) return;
      const rect = container.getBoundingClientRect();
      const progress = Math.max(0, Math.min(1, 1 - rect.top / vh));
      if (Math.abs(progress - lastProgress) >= 0.001) {
        root.style.setProperty('--mv-progress', progress.toFixed(3));
        if (mainVisual) {
          mainVisual.classList.toggle('is-fvFaded', progress >= fadeOutAt);
        }
        lastProgress = progress;
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('lenis-scroll', onScroll);
    window.addEventListener('resize', onScroll);
  }

  commonSpaceSwitcher() {
    const root = document.getElementById('commonSpaceSect');
    if (!root) return;

    const cards = Array.from(root.querySelectorAll('.commonSpaceCard[data-target]'));
    if (!cards.length) return;
    const targets = cards.map((c) => c.dataset.target);

    const setActive = (target) => {
      root.querySelectorAll('[data-target]').forEach((el) => {
        el.classList.toggle('is-active', el.dataset.target === target);
      });
    };

    let index = Math.max(0, cards.findIndex((c) => c.classList.contains('is-active')));

    // PC / SP とも 4 秒ごとに自動で次の画像へ
    let timer = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        index = (index + 1) % targets.length;
        setActive(targets[index]);
      }, 4000);
    };
    const restart = () => {
      if (timer) clearInterval(timer);
      timer = null;
      start();
    };

    // 要素が画面に入ったら自動切り替えを開始（一度だけ）
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        start();
        obs.unobserve(entry.target);
      });
    }, { threshold: 0 });
    io.observe(root);

    // ピンをクリックしたら該当画像に切替＋タイマーをリセット（そこから再び自動進行）
    root.querySelectorAll('.js-commonSpacePin').forEach((pin) => {
      pin.addEventListener('click', () => {
        const target = pin.dataset.target;
        if (!target) return;
        index = targets.indexOf(target);
        setActive(target);
        restart();
      });
    });
  }

  // plan のカード重なり（GSAP 不使用、location / design と同じ sticky バッファ方式）。
  // head + stage をまとめた __sticky を、下端が画面下端に付く位置（top = 100vh - sticky高さ）
  // で固定（head も一体で固定）。確保したスクロール量のあいだ後続カードを下から重ねる。
  planSectStacking() {
    const pin = document.querySelector('.js-planSect-stack');
    if (!pin) return;
    const sticky = pin.querySelector('.js-planSect-stack-sticky');
    const items = pin.querySelectorAll('.js-planSect-stack-item');
    if (!sticky || !items.length) return;

    const total = items.length;
    // 各カードが迫り上がる前に、前カードを見せておく「待ち」の割合（0〜1。1フェーズ内の先頭ぶん）。
    const HOLD = 0.22;
    let stickyH = 0;
    let span = 0;
    let startTop = 0;
    let ticking = false;
    let lastProgress = -1;

    const layout = () => {
      const vh = window.innerHeight;
      sticky.style.top = '';
      stickyH = sticky.offsetHeight;
      // 1 枚重なるごとに「迫り上がり 1 画面分 ＋ 待ち（HOLD）」。待ちの分だけ span を伸ばす。
      span = Math.max(0, (total - 1) * vh / (1 - HOLD));
      sticky.style.top = `${vh - stickyH}px`;
      pin.style.height = `${stickyH + span}px`;
      startTop = vh - stickyH;
    };

    const update = () => {
      ticking = false;
      const scrolled = startTop - pin.getBoundingClientRect().top;
      const progress = span > 0 ? Math.max(0, Math.min(1, scrolled / span)) : 0;
      if (Math.abs(progress - lastProgress) < 0.001) return;
      lastProgress = progress;

      items.forEach((item, i) => {
        if (i === 0) {
          item.style.setProperty('--reveal', '1');
          return;
        }
        if (total <= 1) return;
        const phaseStart = (i - 1) / (total - 1);
        const phaseEnd = i / (total - 1);
        const local = (progress - phaseStart) / (phaseEnd - phaseStart);
        // フェーズ先頭の HOLD ぶんは前カードを見せたまま待機（reveal 0）、残りで 0→1 に迫り上げる。
        const reveal = Math.max(0, Math.min(1, (local - HOLD) / (1 - HOLD)));
        item.style.setProperty('--reveal', reveal.toFixed(3));
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    const onResize = () => {
      layout();
      update();
    };

    layout();
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('lenis-scroll', onScroll);
    window.addEventListener('resize', onResize);

    // 画像ロードなどで sticky 高さが変わったら取り直す
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(() => onResize());
      ro.observe(sticky);
    } else {
      sticky.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', onResize, { once: true });
      });
    }
  }
}
